import { type SupplierPaidOutstandingRow } from '@utazzo/shared';
import { UzInput } from '@utazzo/ui';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { FyRangeControls, useFyRange } from '../components/FyRangeControls';
import { formatDateDMY, formatInrPaise } from '../invoices.helpers';
import { useSupplierPaidOutstanding } from '../use-invoices';

import {
  ReportListScreen,
  type ReportColumn,
} from '@/components/report-list-screen/ReportListScreen';

/**
 * Supplier Paid But Client Outstanding - report #11. Cash-flow-risk invoices
 * where the supplier was paid but the customer has paid nothing. Built on the
 * shared ReportListScreen shell.
 */

const rupees = (paise: string): number => Number(paise) / 100;
const sumPaise = (
  rows: ReadonlyArray<SupplierPaidOutstandingRow>,
  pick: (r: SupplierPaidOutstandingRow) => string,
) => formatInrPaise(rows.reduce((acc, r) => acc + BigInt(pick(r)), 0n).toString());

const money = (
  header: string,
  pick: (r: SupplierPaidOutstandingRow) => string,
): ReportColumn<SupplierPaidOutstandingRow> => ({
  header,
  align: 'right',
  value: (r) => rupees(pick(r)),
  render: (r) => formatInrPaise(pick(r)),
  total: (rows) => sumPaise(rows, pick),
});

export const SupplierPaidOutstandingPage = (): React.JSX.Element => {
  const fyState = useFyRange();
  const { from, to } = fyState;
  const [q, setQ] = useState('');

  const query = { from, to, ...(q.trim() ? { search: q.trim() } : {}) };
  const reportQuery = useSupplierPaidOutstanding(query);
  const rows = reportQuery.data?.rows ?? [];

  const columns: ReportColumn<SupplierPaidOutstandingRow>[] = [
    {
      header: 'Invoice No.',
      value: (r) => r.invoiceNumber,
      render: (r) => (
        <Link to={`/invoices/${r.invoiceId}`} className="text-primary hover:underline">
          {r.invoiceNumber}
        </Link>
      ),
    },
    { header: 'Date', value: (r) => formatDateDMY(r.invoiceDate ?? '') },
    { header: 'Customer', value: (r) => r.customerName ?? '' },
    { header: 'Agent', value: (r) => r.agentName ?? '' },
    money('Grand Total', (r) => r.grandTotalPaise),
    money('Supplier Paid', (r) => r.supplierPaidPaise),
    money('Outstanding', (r) => r.outstandingPaise),
    {
      header: 'Days',
      align: 'right',
      value: (r) => r.daysOutstanding,
      render: (r) => String(r.daysOutstanding),
    },
  ];

  return (
    <ReportListScreen
      title="Supplier Paid But Client Outstanding"
      description="Cash-flow risk: supplier already paid, but the customer has paid nothing yet."
      columns={columns}
      rows={rows}
      getRowKey={(r) => r.invoiceId}
      loading={reportQuery.isLoading}
      error={reportQuery.isError}
      emptyMessage="No at-risk invoices for these filters."
      exportName="supplier-paid-client-outstanding"
      search={
        <UzInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search invoice / customer / agent..."
          containerClassName="max-w-md"
        />
      }
      filters={<FyRangeControls state={fyState} />}
    />
  );
};
